"use client"

import { AnimatePresence, motion, type HTMLMotionProps } from "motion/react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from "react"
import { Link } from "@/components/ui/link"
import { Spinner } from "./spinner"

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2",
    // we want to set pointer events to show cursor wait cursor
    "data-[loading]:!cursor-wait data-[loading]:pointer-events-auto",
    // we only want to apply disabled styles when the button is explicitly disabled
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-blue-9 shadow hover:bg-blue-10 active:bg-blue-11 text-blue-1 focus-visible:ring-blue-7",
        destructive:
          "bg-red-9 text-red-1 shadow-sm hover:bg-red-10 active:bg-red-11 focus-visible:ring-red-7",
        outline:
          "border border-slate-7 bg-slate-1 shadow-sm hover:bg-slate-2 active:bg-slate-3 text-slate-12 hover:border-slate-8 focus-visible:ring-slate-8",
        secondary:
          "bg-blue-3 shadow hover:bg-blue-4 active:bg-blue-5 text-blue-11 focus-visible:ring-blue-7",
        ghost:
          "hover:bg-slate-4 active:bg-slate-5 focus-visible:ring-slate-7 text-slate-12",
        link: "text-slate-12 underline-offset-4 hover:underline font-medium",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

interface BaseButtonProps extends VariantProps<typeof buttonVariants> {
  className?: string
  children: ReactNode
  ref?: ForwardedRef<"button" | "a">
  disabled?: boolean
  loading?: boolean
}

export type ButtonProps = BaseButtonProps &
  (
    | Omit<HTMLMotionProps<"button">, "className">
    | Omit<ComponentPropsWithoutRef<typeof Link>, "className">
  )

export function Button({
  children,
  className,
  variant,
  size,
  disabled = false,
  loading = false,
  ref,
  ...props
}: ButtonProps) {
  return "href" in props ? (
    <Link
      unstyled
      {...props}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
      className={cn(
        buttonVariants({ variant, size, className }),
        "relative transition",
      )}
      data-disabled={disabled ? disabled : undefined}
      data-loading={loading ? loading : undefined}
    >
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <motion.button
      {...props}
      layout
      // only look for layout changes when loading changes
      layoutDependency={loading}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      className={cn(
        "relative cursor-default transition-none",
        buttonVariants({ variant, size, className }),
      )}
      // set disabled whenever we're loading or disabled
      disabled={disabled || loading}
      // only set disabled when explicitly disabled
      data-disabled={disabled ? disabled : undefined}
      data-loading={loading ? loading : undefined}
    >
      <AnimatePresence mode="popLayout">
        {loading && (
          <motion.span
            key="button-spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Spinner className="mr-2" />
          </motion.span>
        )}
        <motion.span layout className="inline-flex items-center justify-center">
          <TouchTarget>{children}</TouchTarget>
        </motion.span>
      </AnimatePresence>
    </motion.button>
  )
}
Button.displayName = "Button"

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
